// src/pages/ListaTecnicos.js
import React, { useEffect, useState } from 'react';
import { getTecnicos } from '../services/tecnicoService';
import { utils, writeFile } from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { ArrowUpDown, FileDown, FileText } from 'lucide-react';

const ListaTecnicos = ({ token }) => {
  const [tecnicos, setTecnicos] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [orden, setOrden] = useState({ campo: 'nombre', asc: true });
  const [error, setError] = useState('');
  const [pagina, setPagina] = useState(1);
  const porPagina = 10;

  useEffect(() => {
    const cargar = async () => {
      try {
        const data = await getTecnicos(token);
        setTecnicos(data);
      } catch (err) {
        setError('No se pudo cargar la lista de técnicos');
      }
    };
    cargar();
  }, [token]);

  const filtrar = (lista) => {
    return lista.filter(t => t.nombre.toLowerCase().includes(filtro.toLowerCase()));
  };

  const ordenar = (lista) => {
    return [...lista].sort((a, b) => {
      const valorA = a[orden.campo]?.toLowerCase?.() || '';
      const valorB = b[orden.campo]?.toLowerCase?.() || '';
      if (valorA < valorB) return orden.asc ? -1 : 1;
      if (valorA > valorB) return orden.asc ? 1 : -1;
      return 0;
    });
  };

  const cambiarOrden = (campo) => {
    setOrden((prev) => ({
      campo,
      asc: prev.campo === campo ? !prev.asc : true,
    }));
  };

  const tecnicosFiltrados = ordenar(filtrar(tecnicos));
  const totalPaginas = Math.ceil(tecnicosFiltrados.length / porPagina);
  const tecnicosPaginados = tecnicosFiltrados.slice((pagina - 1) * porPagina, pagina * porPagina);

  const exportarExcel = () => {
    const hoja = utils.json_to_sheet(tecnicosFiltrados);
    const libro = utils.book_new();
    utils.book_append_sheet(libro, hoja, 'Técnicos');
    writeFile(libro, 'tecnicos.xlsx');
  };

  const exportarPDF = () => {
    const doc = new jsPDF();
    doc.autoTable({
      head: [['Nombre', 'Especialidad', 'Teléfono', 'Zonas', 'Servicios', 'Tarifas']],
      body: tecnicosFiltrados.map(t => [t.nombre, t.especialidad, t.telefono, t.zonas, t.servicios, t.tarifas]),
    });
    doc.save('tecnicos.pdf');
  };

  return (
    <div className="p-4 w-full">
      <h2 className="text-xl font-bold mb-4 text-center md:text-left">Listado de Técnicos Registrados</h2>
      {error && <p className="text-red-500 mb-4 text-center md:text-left">{error}</p>}

      <div className="flex flex-col lg:flex-row justify-between mb-4 gap-2">
        <input
          type="text"
          placeholder="Filtrar por nombre..."
          className="p-2 border rounded w-full lg:w-1/3"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
        />
        <div className="flex flex-row gap-2 justify-start">
          <button onClick={exportarExcel} className="bg-green-600 text-white p-2 rounded" title="Exportar a Excel">
            <FileDown size={18} />
          </button>
          <button onClick={exportarPDF} className="bg-red-600 text-white p-2 rounded" title="Exportar a PDF">
            <FileText size={18} />
          </button>
        </div>
      </div>

      <div className="w-full overflow-x-auto">
        <table className="w-full bg-white text-sm">
          <thead className="bg-gray-100">
            <tr>
              {['nombre', 'especialidad', 'telefono', 'zonas', 'servicios', 'tarifas'].map((col) => (
                <th key={col} className="p-2 whitespace-nowrap cursor-pointer text-left" onClick={() => cambiarOrden(col)}>
                  <div className="flex items-center gap-1">
                    {col.charAt(0).toUpperCase() + col.slice(1)}
                    <ArrowUpDown size={14} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tecnicosPaginados.map((t) => (
              <tr key={t.id} className="border-t hover:bg-gray-50">
                <td className="p-2 whitespace-nowrap">{t.nombre}</td>
                <td className="p-2 whitespace-nowrap">{t.especialidad}</td>
                <td className="p-2 whitespace-nowrap">{t.telefono}</td>
                <td className="p-2 whitespace-nowrap">{t.zonas}</td>
                <td className="p-2 whitespace-nowrap">{t.servicios}</td>
                <td className="p-2 whitespace-nowrap">{t.tarifas}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap justify-center mt-4 gap-2">
        {[...Array(totalPaginas)].map((_, i) => (
          <button
            key={i}
            className={`px-3 py-1 border rounded ${pagina === i + 1 ? 'bg-blue-600 text-white' : 'bg-white'}`}
            onClick={() => setPagina(i + 1)}
          >
            {i + 1}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ListaTecnicos;
